import { App } from "@capacitor/app";
import { Preferences } from "@capacitor/preferences";
import { datadogRum } from "@datadog/browser-rum";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import semver from "semver";

import { useTranslation } from "@/core/i18n";
import { getAppVersion } from "api/methods";
import { Button } from "atoms/button/Button";
import Analytics from "modules/analytics/AnalyticsService/AnalyticsService";
import { useFeatureFlag } from "services/Flags";
import { isIosPlatform, isNativeMobileApp } from "utils/platform.helpers";

const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.repeatmd.mobile";

const APP_STORE_URL = "https://apps.apple.com/us/app/myrepeat-patient-rewards/id6448703360";

const RELOAD_GUARD_KEY = "mobileAppVersionReloadAttempt";
const RELOAD_GUARD_WINDOW_MS = 1000 * 60 * 2;

// Max frequency for checking `/app/version` (we trigger off route changes).
const VERSION_CHECK_THROTTLE_MS = 1000 * 60 * 10;

const normalizeVersion = (version: string): string => {
  return version.replace("-dev", "");
};

const toSemVer = (version: string | null): semver.SemVer | null => {
  if (!version) return null;

  const normalized = normalizeVersion(version);
  return semver.coerce(normalized);
};

const getStoreUrl = (isIos: boolean): string => {
  return isIos ? APP_STORE_URL : PLAY_STORE_URL;
};

const getInstalledAppVersion = async (isMobileApp: boolean): Promise<string | null> => {
  if (!isMobileApp) return null;

  try {
    const { version } = await App.getInfo();
    const normalized = normalizeVersion(version);
    if (!semver.valid(normalized)) {
      throw new Error("Invalid native app version");
    }
    return normalized;
  } catch {
    // A failed native read is not evidence that an update is required. Do not report
    // the plugin's raw error or substitute the mutable web-refresh preference.
    datadogRum.addError(new Error("[RMD]: Unable to read a valid native app version"));
    return null;
  }
};

const getLastRefreshedAppVersion = async (isMobileApp: boolean): Promise<string | null> => {
  if (!isMobileApp) return null;

  try {
    const { value } = await Preferences.get({ key: "mobileAppVersion" });
    if (!value) {
      console.warn("[RMD]: mobileAppVersion not found in Preferences.");
      return null;
    }
    return normalizeVersion(value);
  } catch (e) {
    console.error(e);
    return null;
  }
};

type ReloadGuardState = {
  attemptedAt: number;
  latestVersion: string;
};

const getReloadGuardState = async (isMobileApp: boolean): Promise<ReloadGuardState | null> => {
  if (!isMobileApp) return null;

  try {
    const { value } = await Preferences.get({ key: RELOAD_GUARD_KEY });
    if (!value) return null;

    const parsed = JSON.parse(value) as Partial<ReloadGuardState>;
    if (typeof parsed.attemptedAt !== "number" || typeof parsed.latestVersion !== "string") {
      return null;
    }

    return {
      attemptedAt: parsed.attemptedAt,
      latestVersion: parsed.latestVersion,
    };
  } catch (e) {
    console.error(e);
    return null;
  }
};

const setReloadGuardState = async (
  isMobileApp: boolean,
  state: ReloadGuardState,
): Promise<boolean> => {
  if (!isMobileApp) return false;

  try {
    await Preferences.set({
      key: RELOAD_GUARD_KEY,
      value: JSON.stringify(state),
    });
    return true;
  } catch (e) {
    console.error(e);
    return false;
  }
};

export default function AppVersionCheck(): JSX.Element | null {
  const { t } = useTranslation();
  const isMobileApp = isNativeMobileApp();
  const location = useLocation();
  const hasReloadedRef = useRef(false);
  const hasTrackedForceUpdateRef = useRef(false);
  const lastVersionRequestAtRef = useRef<number>(0);
  const [installedAppVersion, setInstalledAppVersion] = useState<string | null>(null);
  const [lastRefreshedAppVersion, setLastRefreshedAppVersion] = useState<string | null>(null);
  const [hasCheckedInstalledAppVersion, setHasCheckedInstalledAppVersion] = useState(false);

  const {
    data: appVersion = null,
    refetch: refetchLatestVersion,
    isFetching: isFetchingLatestVersion,
  } = useQuery({
    ...getAppVersion.keyed(),
    select: ({ version }: { version: string }) => version,
    // Since we manually refetch on route changes (throttled), keep this aligned with the same window.
    staleTime: VERSION_CHECK_THROTTLE_MS,
    enabled: false,
  });

  const latestMobileAppVersion = appVersion ? normalizeVersion(appVersion) : null;

  const installedSemVer = useMemo(() => {
    return toSemVer(installedAppVersion);
  }, [installedAppVersion]);

  const latestSemVer = useMemo(() => {
    return toSemVer(latestMobileAppVersion);
  }, [latestMobileAppVersion]);

  const lastRefreshedSemVer = toSemVer(lastRefreshedAppVersion);

  const isDev = useFeatureFlag("isDev");

  const appStoreUrl = useMemo(() => {
    return getStoreUrl(isIosPlatform());
  }, []);

  const handleUpdateClick = () => {
    window.location.href = appStoreUrl;
  };

  // Check for updates on every route change, but throttle to at most once every 10 minutes.
  useEffect(() => {
    if (!isMobileApp) return;
    if (isFetchingLatestVersion) return;

    const now = Date.now();
    if (now - lastVersionRequestAtRef.current < VERSION_CHECK_THROTTLE_MS) return;

    lastVersionRequestAtRef.current = now;
    void refetchLatestVersion();
  }, [
    isMobileApp,
    isFetchingLatestVersion,
    location.pathname,
    location.search,
    refetchLatestVersion,
  ]);

  const shouldForceUpdate = useMemo(() => {
    if (!isMobileApp) return false;
    if (!hasCheckedInstalledAppVersion) return false;

    if (!installedSemVer) return false;
    if (!latestSemVer) return false;

    return latestSemVer.major > installedSemVer.major;
  }, [hasCheckedInstalledAppVersion, installedSemVer, isMobileApp, latestSemVer]);

  // Web reloads cannot change App.getInfo().version. Keep their existing persisted
  // version and cooldown independent of the native store-update decision.
  const shouldAppReload = Boolean(
    isMobileApp &&
    hasCheckedInstalledAppVersion &&
    lastRefreshedSemVer &&
    latestSemVer &&
    lastRefreshedSemVer.major === latestSemVer.major &&
    semver.gt(latestSemVer, lastRefreshedSemVer),
  );

  // Synchronize with the native App plugin on mount and when returning from the store.
  useEffect(() => {
    if (!isMobileApp) {
      setInstalledAppVersion(null);
      setLastRefreshedAppVersion(null);
      setHasCheckedInstalledAppVersion(false);
      return;
    }

    let canceled = false;
    let latestRead = 0;

    const readInstalledVersion = async () => {
      const read = ++latestRead;
      const nextInstalled = await getInstalledAppVersion(isMobileApp);
      if (canceled || read !== latestRead) return;

      setInstalledAppVersion(nextInstalled);
      setHasCheckedInstalledAppVersion(true);
    };

    // Read the web marker once: a pending reload can advance its persisted value,
    // but foregrounding must not interpret that write as a completed refresh.
    void getLastRefreshedAppVersion(isMobileApp).then((version) => {
      if (!canceled) setLastRefreshedAppVersion(version);
    });
    void readInstalledVersion();
    const listener = App.addListener("appStateChange", ({ isActive }) => {
      if (isActive) void readInstalledVersion();
    });
    // Old shells can fail to subscribe; retain the mount-time check and report only
    // a fixed diagnostic, never raw native error details.
    void listener.catch(() => {
      datadogRum.addError(new Error("[RMD]: Unable to subscribe to native app state changes"));
    });

    return () => {
      canceled = true;
      void listener
        .then((handle) => handle.remove())
        .catch(() => {
          datadogRum.addError(new Error("[RMD]: Unable to clean up native app state listener"));
        });
    };
  }, [isMobileApp]);

  useEffect(() => {
    if (!isMobileApp) return;
    if (!hasCheckedInstalledAppVersion) return;
    if (!installedAppVersion) return;

    datadogRum.setGlobalContextProperty("installedAppVersion", installedAppVersion);
  }, [isMobileApp, hasCheckedInstalledAppVersion, installedAppVersion]);

  // When the latest version is a same-major upgrade, we're going to hard-reload to refresh
  // the web bundle/assets. Persisting the latest version here helps avoid repeated reload
  // attempts across app restarts.
  //
  // This preference is only a web-refresh marker; native eligibility never reads it.
  useEffect(() => {
    if (!isMobileApp) return;
    if (!hasCheckedInstalledAppVersion) return;
    if (!shouldAppReload) return;
    if (!latestMobileAppVersion) return;

    (async () => {
      try {
        await Preferences.set({
          key: "mobileAppVersion",
          value: latestMobileAppVersion,
        });
      } catch (e) {
        console.error(e);
      }
    })();
  }, [hasCheckedInstalledAppVersion, isMobileApp, latestMobileAppVersion, shouldAppReload]);

  /*
   * Reload strategy (native mobile only):
   * - If the current app version (from Preferences) is on the same major version as the
   *   latest version, but behind, we trigger a single hard reload so the web bundle/assets
   *   can be refreshed.
   *
   * Guard rails to prevent reload loops:
   * - `hasReloadedRef`: prevents multiple reloads within the same mount/runtime.
   * - Preferences-based timestamp + target version: persists across reloads/remounts.
   *   If we already attempted a reload very recently for the same latest version,
   *   we skip reloading again.
   *
   * Why 2 minutes:
   * - Long enough to avoid rapid reload loops when a reload doesn't resolve the mismatch
   *   (e.g., offline, CDN/cache delay, temporary network issues).
   * - Short enough to allow a retry shortly after conditions improve.
   */
  useEffect(() => {
    if (!isMobileApp) return;
    if (!shouldAppReload) return;
    if (!latestMobileAppVersion) return;

    if (hasReloadedRef.current) return;

    let canceled = false;

    (async () => {
      const lastAttempt = await getReloadGuardState(isMobileApp);
      if (canceled) return;

      const now = Date.now();
      const isSameTarget = lastAttempt?.latestVersion === latestMobileAppVersion;
      const isRecent =
        typeof lastAttempt?.attemptedAt === "number" &&
        now - lastAttempt.attemptedAt < RELOAD_GUARD_WINDOW_MS;

      if (isSameTarget && isRecent) {
        console.warn("[RMD]: Skipping app reload: recent reload attempt detected.", {
          installedAppVersion,
          latestMobileAppVersion,
          attemptedAt: lastAttempt.attemptedAt,
        });
        return;
      }

      // Defensive: if dependencies change while awaiting the Preferences read above,
      // React will run this effect again before this async work completes. Re-check
      // the ref so a stale invocation doesn't trigger a reload.
      if (hasReloadedRef.current) return;

      // Take an in-memory lock before awaiting persistence so only one invocation
      // can proceed (prevents two concurrent invocations from both attempting to
      // persist + reload).
      hasReloadedRef.current = true;

      // Mark the guard before reloading. If we can't persist it, don't reload to avoid
      // potential reload loops across app starts.
      const didPersistReloadAttempt = await setReloadGuardState(isMobileApp, {
        attemptedAt: now,
        latestVersion: latestMobileAppVersion,
      });

      if (canceled) {
        hasReloadedRef.current = false;
        return;
      }

      if (!didPersistReloadAttempt) {
        hasReloadedRef.current = false;
        return;
      }

      console.log(
        "[RMD]: Reloading app due to version mismatch. Installed version: ",
        installedAppVersion,
        "Latest version: ",
        latestMobileAppVersion,
      );
      window.location.reload();
    })();

    return () => {
      canceled = true;
    };
  }, [isMobileApp, shouldAppReload, installedAppVersion, latestMobileAppVersion]);

  useEffect(() => {
    if (!isMobileApp) return;
    if (!hasCheckedInstalledAppVersion) return;
    if (!shouldForceUpdate) return;
    if (isDev) return;
    if (hasTrackedForceUpdateRef.current) return;

    hasTrackedForceUpdateRef.current = true;
    Analytics.track("Forcing app update", {
      installedAppVersion,
      requiredVersion: latestMobileAppVersion,
    });
  }, [
    hasCheckedInstalledAppVersion,
    installedAppVersion,
    isDev,
    isMobileApp,
    latestMobileAppVersion,
    shouldForceUpdate,
  ]);

  const renderForceUpdate = () => (
    <div
      data-modal-backdrop="static"
      className="pointer-events-none fixed inset-0 z-[999] grid h-screen w-screen place-items-center bg-black bg-opacity-60 backdrop-blur-md transition-opacity duration-300"
    >
      <div className="pointer-events-none relative w-auto translate-y-[-50px] transition-all duration-300 ease-in-out min-[576px]:mx-auto min-[576px]:mt-7 min-[576px]:max-w-[270px]">
        <div className="shadow-4 dark:bg-surface-dark pointer-events-auto relative flex w-full flex-col rounded-md border-none bg-white bg-clip-padding text-current outline-none">
          <div className="relative flex justify-center p-4 pb-2 text-center text-brand-color">
            {t("appVersionCheck.forceUpdateMessage")}
          </div>
          <div className="flex flex-col items-center justify-end rounded-b-md border-t border-brand-color/30 p-4">
            <Button onClick={handleUpdateClick}>{t("appVersionCheck.updateButton")}</Button>
          </div>
        </div>
      </div>
    </div>
  );

  if (!isMobileApp) {
    return null;
  }

  if (hasCheckedInstalledAppVersion && shouldForceUpdate && !isDev) {
    return renderForceUpdate();
  }

  return null;
}
